import React from "react";
import Plot from "react-plotly.js";
import {
  TrendingUp,
  DollarSign,
  ShoppingCart,
  ChartSpline,
} from "lucide-react";
import { Box, styled } from "@mui/material";
import MainLayout from "../Layout/MainLayout";

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: theme.spacing(2, 0),
  ...theme.mixins.toolbar,
}));

// Sample data generators with optional mean reversion
const generateTimeSeriesData = (
  days,
  baseValue,
  trend,
  volatility,
  meanRevert = false
) => {
  const data = [];
  const dates = [];
  let currentValue = baseValue;

  for (let i = 0; i < days; i++) {
    const date = new Date();
    date.setDate(date.getDate() - days + i);
    dates.push(date.toISOString().split("T")[0]);

    const noise = (Math.random() - 0.5) * volatility;

    if (meanRevert) {
      currentValue += trend + noise + (baseValue - currentValue) * 0.05;
    } else {
      currentValue += trend + noise;
    }

    data.push(Math.max(0, currentValue));
  }

  return { dates, values: data };
};

const Dashboard = () => {
  const [dataSet, setDataSet] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [showComparison, setShowComparison] = React.useState(false);

  const dataSets = [
    {
      name: "Q1 Performance",
      revenue: { base: 50000, trend: 500, volatility: 5000 },
      users: { base: 10000, trend: 100, volatility: 500 },
      orders: { base: 500, trend: 5, volatility: 50 },
      conversion: { base: 2.5, trend: 0.01, volatility: 0.2 },
    },
    {
      name: "Q2 Growth",
      revenue: { base: 75000, trend: 800, volatility: 7000 },
      users: { base: 15000, trend: 200, volatility: 800 },
      orders: { base: 750, trend: 12, volatility: 80 },
      conversion: { base: 3.2, trend: 0.02, volatility: 0.3 },
    },
    {
      name: "Q3 Peak",
      revenue: { base: 95000, trend: 1200, volatility: 9000 },
      users: { base: 22000, trend: 300, volatility: 1200 },
      orders: { base: 1200, trend: 20, volatility: 120 },
      conversion: { base: 4.1, trend: 0.03, volatility: 0.4 },
    },
    {
      name: "Q4 Holiday",
      revenue: { base: 120000, trend: 1500, volatility: 12000 },
      users: { base: 28000, trend: 400, volatility: 1500 },
      orders: { base: 1800, trend: 35, volatility: 180 },
      conversion: { base: 5.2, trend: 0.04, volatility: 0.5 },
    },
  ];

  // Keep revenueData constant across dataset changes
  const revenueDataRef = React.useRef(
    generateTimeSeriesData(
      30,
      dataSets[0].revenue.base,
      dataSets[0].revenue.trend,
      dataSets[0].revenue.volatility
    )
  );

  // Users data will change on handleDataChange
// Users data will now roughly follow revenueDataRef
// Users data will now roughly follow revenueDataRef with ±5000 difference
const [usersData, setUsersData] = React.useState(() => {
  const revenueValues = revenueDataRef.current.values;
  const data = revenueValues.map((val) => val + (Math.random() - 0.5) * 10000); // ±5k
  return { dates: revenueDataRef.current.dates, values: data };
});



  const currentDataSet = dataSets[dataSet];

  const ordersData = generateTimeSeriesData(
    30,
    currentDataSet.orders.base,
    currentDataSet.orders.trend,
    currentDataSet.orders.volatility
  );

  const conversionData = generateTimeSeriesData(
    30,
    currentDataSet.conversion.base,
    currentDataSet.conversion.trend,
    currentDataSet.conversion.volatility
  );

  const handleDataChange = () => {
    setIsLoading(true);
    setTimeout(() => {
      setDataSet((prev) => {
        const next = (prev + 1) % dataSets.length;

        // Only regenerate usersData
        const nextUsersData = {
          dates: revenueDataRef.current.dates,
          values: revenueDataRef.current.values.map(
            (val) => val + (Math.random() - 0.5) * 10000 // ±5k
          ),
        };
        setUsersData(nextUsersData);
        
        

        setShowComparison(true);
        return next;
      });
      setIsLoading(false);
    }, 500);
  };

  // Chart config
  const chartConfig = { displayModeBar: false, responsive: true };
  const layoutBase = {
    // margin: { l: 70, r: 20, t: 20, b: 70 }, // increase bottom for x-axis title
    xaxis: { title: "Weeks", showgrid: false, zeroline: true },
    yaxis: { title: "# No of subscribers", gridcolor: "#f0f0f0", zeroline: false },
    autosize: true,
    responsive: true,
    showlegend: true,
    legend: {
      orientation: "h",
      x: 0.5,
      xanchor: "center",
      y: -0.3,
      yanchor: "top",
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pl-6 pr-6">
      <div id="homePage" style={{ position: "relative" }}>
        <Box
          className="mainLayout"
          sx={{ display: "flex", overflow: "hidden" }}
        >
          <MainLayout />
          <Box component="main" sx={{ flexGrow: 1, p: 1 }}>
            <DrawerHeader />
            <DrawerHeader style={{ minHeight: "0px", paddingTop: "3px" }} />
            <div id="mainHome" style={{ overflowY: "auto" }}>
              <div className="mb-8">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h1 className="text-4xl font-bold text-slate-800 mb-2">
                      Demand Forecast
                    </h1>
                    <p className="text-slate-600">
                      Real-time insights and performance metrics for Demand
                      forecast generated based on uploaded files.
                    </p>
                  </div>
                  <button
                    onClick={handleDataChange}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium rounded-lg transition-all duration-200 shadow-sm hover:shadow-md disabled:cursor-not-allowed border-none"
                  >
                    <ChartSpline className="w-4 h-4" />
                    {isLoading ? "Loading..." : "AI News Analyzer"}
                  </button>
                </div>
              </div>

              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">
                        Total New Subscribers
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        {Math.round(
                          ordersData.values[ordersData.values.length - 1]
                        ).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-orange-100 rounded-lg">
                      <ShoppingCart className="w-6 h-6 text-orange-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">
                        Total Revenue
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        $
                        {revenueDataRef.current.values[
                          revenueDataRef.current.values.length - 1
                        ].toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <DollarSign className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-600">
                        Conversion Rate
                      </p>
                      <p className="text-2xl font-bold text-slate-900">
                        {conversionData.values[
                          conversionData.values.length - 1
                        ].toFixed(2)}
                        %
                      </p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Charts */}
              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
                  <h2 className="text-xl font-semibold text-slate-800 mb-4">
                    {showComparison
                      ? "Demand Forecast for FWA Subscribers"
                      : "Demand Forecast for FWA Subscribers"}
                  </h2>

                  {showComparison ? (
                    <Plot
                      data={[
                        {
                          x: revenueDataRef.current.dates,
                          y: revenueDataRef.current.values,
                          type: "scatter",
                          mode: "lines+markers",
                          line: { color: "#3B82F6", width: 3, shape: "spline" },
                          name: "Original Forecast from Kinaxis",
                        },
                        {
                          x: revenueDataRef.current.dates,
                          y: usersData.values,
                          type: "scatter",
                          mode: "lines+markers",
                          line: { color: "#10B981", width: 3, shape: "spline" },
                          name: "AI Revised Forecast",
                        },
                      ]}
                      // layout={{
                      //   ...layoutBase,
                      //   yaxis: {
                      //     ...layoutBase.yaxis,
                      //     title: "Subscribers",
                      //     range: [
                      //       0,
                      //       Math.max(...revenueDataRef.current.values) + 10000,
                      //     ],
                      //   },
                      //   xaxis: { ...layoutBase.xaxis, title: "Date" },
                      //   margin: { l: 50, r: 20, t: 20, b: 40 },
                      // }}
                      layout={{
                        ...layoutBase,
                        margin: { l: 70, r: 20, t: 20, b: 70 }, // increase bottom for x-axis title
                        xaxis: {
                          ...layoutBase.xaxis,
                          title: { text: "Week", font: { size: 14 } },
                          automargin: true,
                        },
                        yaxis: {
                          ...layoutBase.yaxis,
                          title: { text: "# No of subscribers", font: { size: 14 } },
                          automargin: true,
                          range: [0, Math.max(...revenueDataRef.current.values) + 10000],
                        },
                      }}
                      
                      config={{ ...chartConfig, responsive: true }}
                      useResizeHandler={true}
                      style={{ width: "100%", height: "400px" }}
                    />
                  ) : (
                    <Plot
                      data={[
                        {
                          x: revenueDataRef.current.dates,
                          y: revenueDataRef.current.values,
                          type: "scatter",
                          mode: "lines+markers",
                          line: { color: "#3B82F6", width: 3, shape: "spline" },
                          fillcolor: "rgba(59, 130, 246, 0.1)",
                          name: "Original Forecast from Kinaxis",
                        },
                      ]}
                      // layout={{
                      //   ...layoutBase,
                      //   yaxis: {
                      //     ...layoutBase.yaxis,
                      //     title: "Original Forecast from Kinaxis",
                      //     range: [
                      //       0,
                      //       Math.max(...revenueDataRef.current.values) + 10000,
                      //     ],
                          
                      //   },
                      //   xaxis: { ...layoutBase.xaxis, title: "Date" },
                      //   margin: { l: 50, r: 20, t: 20, b: 40 },
                      // }}

                      layout={{
                        ...layoutBase,
                        margin: { l: 70, r: 20, t: 20, b: 70 }, // increase bottom for x-axis title
                        xaxis: {
                          ...layoutBase.xaxis,
                          title: { text: "Week", font: { size: 14 } },
                          automargin: true,
                        },
                        yaxis: {
                          ...layoutBase.yaxis,
                          title: { text: "# No of subscribers", font: { size: 14 } },
                          automargin: true,
                          range: [0, Math.max(...revenueDataRef.current.values) + 10000],
                        },
                      }}
                      config={{ ...chartConfig, responsive: true }}
                      useResizeHandler={true}
                      style={{ width: "100%", height: "400px" }}
                    />
                  )}
                </div>
              </div>
            </div>
          </Box>
        </Box>
      </div>
    </div>
  );
};

export default Dashboard;
